import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Phone, X, ChevronLeft, ChevronRight } from 'lucide-react';

const projects = [
  {
    id: 1,
    title: 'Coastal Kitchen Transformation',
    location: 'St. Augustine, FL',
    category: 'Kitchen',
    scope: 'Complete kitchen remodel including custom cabinetry, quartz countertops, subway tile backsplash, and new flooring.',
    materials: 'Shaker-style cabinets, Calacatta quartz, ceramic subway tile, wide-plank LVP',
    timeline: '6 weeks',
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556909172-8c2f041fca1e?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
    ],
  },
  {
    id: 2,
    title: 'Modern Master Bath',
    location: 'Ponte Vedra, FL',
    category: 'Bathroom',
    scope: 'Master bathroom renovation featuring walk-in shower, freestanding tub, dual vanity, and heated floors.',
    materials: 'Porcelain tile, marble accents, chrome fixtures, custom vanity',
    timeline: '5 weeks',
    image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1507089947368-19c1da9775ae?auto=format&fit=crop&w=1200&q=80',
    ],
  },
  {
    id: 3,
    title: 'Open Concept Living',
    location: 'Jacksonville, FL',
    category: 'Interior',
    scope: 'Interior renovation to create open concept living space with new flooring throughout and custom built-ins.',
    materials: 'Engineered hardwood, custom millwork, contemporary paint palette',
    timeline: '4 weeks',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=1200&q=80',
    ],
  },
  {
    id: 4,
    title: 'Elegant Flooring Update',
    location: 'Nocatee, FL',
    category: 'Flooring',
    scope: 'Whole-home flooring replacement with luxury vinyl plank, including transitions and new baseboards.',
    materials: 'COREtec luxury vinyl plank, matching quarter round, premium underlayment',
    timeline: '2 weeks',
    image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1200&q=80',
    ],
  },
  {
    id: 5,
    title: 'Spa-Inspired Guest Bath',
    location: 'St. Johns, FL',
    category: 'Bathroom',
    scope: 'Guest bathroom update with tiled shower, new vanity, lighting, and fixtures.',
    materials: 'Large format tile, floating vanity, matte black fixtures',
    timeline: '3 weeks',
    image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1200&q=80',
    ],
  },
  {
    id: 6,
    title: 'Chef\'s Kitchen Upgrade',
    location: 'Palm Coast, FL',
    category: 'Kitchen',
    scope: 'Kitchen update with painted cabinets, new countertops, backsplash, and under-cabinet lighting.',
    materials: 'Benjamin Moore paint, granite countertops, glass mosaic backsplash',
    timeline: '4 weeks',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
    ],
  },
];

const categories = ['All', 'Kitchen', 'Bathroom', 'Interior', 'Flooring'];

export const Projects = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const filteredProjects = activeCategory === 'All'
    ? projects
    : projects.filter((p) => p.category === activeCategory);

  const openLightbox = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const closeLightbox = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === selectedProject.gallery.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedProject.gallery.length - 1 : prev - 1
      );
    }
  };

  return (
    <main data-testid="projects-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="w-16 h-px bg-[#C6A87C] mb-8" />
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">Our Projects</h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              A curated selection of our recent transformations across Northeast Florida. Each project reflects our commitment to quality craftsmanship and attention to detail.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                data-testid={`filter-${category.toLowerCase()}`}
                className={`px-6 py-2 text-xs font-semibold uppercase tracking-widest transition-colors ${
                  activeCategory === category
                    ? 'bg-[#0A0A0A] text-white'
                    : 'bg-transparent text-gray-600 hover:text-[#0A0A0A]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="group cursor-pointer"
                  onClick={() => openLightbox(project)}
                  data-testid={`project-${project.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden mb-4">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#C6A87C]">
                    {project.category}
                  </span>
                  <h3 className="font-heading text-xl mt-2 mb-1">{project.title}</h3>
                  <p className="text-gray-500 text-sm">{project.location}</p>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
            data-testid="project-lightbox"
          >
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white hover:text-[#C6A87C] transition-colors z-10"
              aria-label="Close"
            >
              <X className="w-8 h-8" />
            </button>

            <div
              className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Image */}
              <div className="relative">
                <img
                  src={selectedProject.gallery[currentImageIndex]}
                  alt={selectedProject.title}
                  className="w-full aspect-[4/3] object-cover"
                />
                {selectedProject.gallery.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {selectedProject.gallery.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentImageIndex ? 'bg-[#C6A87C]' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Details */}
              <div className="text-white">
                <span className="text-xs font-semibold uppercase tracking-widest text-[#C6A87C]">
                  {selectedProject.category}
                </span>
                <h2 className="font-heading text-3xl mt-2 mb-2">{selectedProject.title}</h2>
                <p className="text-gray-400 mb-8">{selectedProject.location}</p>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-widest text-[#C6A87C] mb-2">
                      Scope
                    </h4>
                    <p className="text-gray-300 text-sm leading-relaxed">{selectedProject.scope}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-widest text-[#C6A87C] mb-2">
                      Materials
                    </h4>
                    <p className="text-gray-300 text-sm leading-relaxed">{selectedProject.materials}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-widest text-[#C6A87C] mb-2">
                      Timeline
                    </h4>
                    <p className="text-gray-300 text-sm">{selectedProject.timeline}</p>
                  </div>
                </div>

                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 mt-10 bg-[#C6A87C] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
                >
                  Start Your Project
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Ready to Transform Your Space?
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              Let's discuss your vision and create something beautiful together.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:9048636673"
                data-testid="projects-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#C6A87C] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
              >
                <Phone className="w-4 h-4" />
                (904) 863-6673
              </a>
              <Link
                to="/contact"
                data-testid="projects-estimate-btn"
                className="inline-flex items-center justify-center gap-3 bg-white text-[#0A0A0A] px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-gray-100 transition-colors"
              >
                Request Estimate
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
